// Shared mutation helpers — collapse the dozen+ identical
// `onSuccess: () => qc.invalidateQueries({ queryKey: X })` callsites scattered
// across feature hooks into one call.
//
// Why not use the helpers inline in tests? They expose a small surface that
// lets us assert "invalidated keys K1, K2" against `qc._cache` without
// re-implementing the closure shape.
import type { QueryClient, QueryKey } from "./index";

/**
 * Invalidate every passed key on the client. Empty input is a no-op.
 *
 * @example
 *   invalidateAll(qc, adminPaymentKeys.all, adminPaymentKeys.comments(id));
 */
export function invalidateAll(
  qc: QueryClient,
  ...keys: readonly QueryKey[]
): void {
  for (const queryKey of keys) {
    qc.invalidateQueries({ queryKey });
  }
}

/**
 * Builds an `onSuccess` callback that invalidates the supplied keys.
 * Captures the QueryClient by reference; do not reuse across renders.
 *
 * @example
 *   useMutation({ mutationFn, onSuccess: makeInvalidator(qc, profileKeys.profile()) });
 */
export function makeInvalidator(
  qc: QueryClient,
  ...keys: readonly QueryKey[]
): () => void {
  return () => invalidateAll(qc, ...keys);
}
